import React from "react";
import { motion } from "framer-motion";
import { Download, FileText, ArrowUpRight } from "lucide-react";
import { COMPANY } from "../../data/site";

export const BrochureSection = () => {
  return (
    <section
      data-testid="brochure-section"
      className="relative bg-white py-24 md:py-32 border-t border-[#E2E8F0] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10 items-center">
        {/* Brochure visual — mock catalogue book */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="md:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] max-w-sm mx-auto md:mx-0">
            {/* Stack effect — back cover */}
            <div className="absolute inset-0 translate-x-3 translate-y-3 bg-[#E2E8F0] border border-[#CBD5E1]" aria-hidden="true" />
            {/* Front cover */}
            <a
              href={COMPANY.brochure_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brochure-cover-link"
              className="relative block w-full h-full bg-[#0F172A] border border-[#0F172A] overflow-hidden shadow-[0_24px_60px_-24px_rgba(15,23,42,0.45)] group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#1E3A8A]" />
              <div className="absolute inset-0 tech-grid-light opacity-40" />
              <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between">
                <div className="font-mono-wm text-[10px] tracking-[0.3em] uppercase text-[#0EA5E9]">
                  Catalogue · 2026
                </div>
                <FileText className="w-4 h-4 text-white/60" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="w-10 h-px bg-[#0EA5E9] mb-4" />
                <h3 className="font-display uppercase tracking-tight text-white text-xl md:text-3xl leading-[0.95]">
                  Product<br />Catalogue
                </h3>
                <p className="font-mono-wm text-[11px] tracking-[0.25em] uppercase text-white/70 mt-3">
                  Welding Automation · Weld Overlay
                </p>
                <div className="mt-6 inline-flex items-center gap-2 font-mono-wm text-[10px] tracking-[0.3em] uppercase text-white/70 group-hover:text-[#0EA5E9] transition-colors">
                  PDF · 10.2 MB <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </a>
          </div>
        </motion.div>

        {/* Copy + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="md:col-span-7"
        >
          <div className="eyebrow mb-4">// Catalogue · Download</div>
          <h2 className="font-display uppercase tracking-tight text-[#0F172A] text-4xl md:text-5xl leading-[0.95]">
            Download the<br />
            <span className="text-[#0EA5E9]">Weldmatix Product Catalogue</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#475569] max-w-xl leading-relaxed">
            A complete overview of our welding automation and weld overlay systems —
            specifications, configurations, applications and reference projects across
            global industries.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
            {[
              "Pipe Cladding · Twin & Quad-Head Systems",
              "Vertical & OMNI Clad Stations",
              "Continuous Rotating Torch (CRT)",
              "Auto DPT · Butt Welding · Tubewall Overlay"
            ].map((b) => (
              <div key={b} className="flex items-start gap-2.5 text-sm text-[#0F172A]">
                <span className="w-4 h-px bg-[#0EA5E9] mt-2.5 flex-shrink-0" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href={COMPANY.brochure_url}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="brochure-download-btn"
              className="group inline-flex items-center justify-between gap-6 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-7 py-4 text-sm uppercase tracking-[0.2em] font-medium transition-colors min-w-[280px]"
            >
              Download Catalogue (PDF)
              <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
            </a>
            <a
              href={COMPANY.brochure_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brochure-view-online"
              className="inline-flex items-center justify-between gap-6 border border-[#CBD5E1] hover:border-[#0F172A] text-[#0F172A] px-7 py-4 text-sm uppercase tracking-[0.2em] transition-colors min-w-[240px]"
            >
              View Online
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>

          <div className="mt-6 font-mono-wm text-[11px] tracking-[0.2em] uppercase text-[#64748B]">
            PDF · 10.2 MB · Free Download · No registration required
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BrochureSection;
